import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import ExpenseAdd from "./ExpenseAdd";
import ExpenseEdit from "./ExpenseEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";

import { formatJalaliDateTime } from "../utils/dateFormat";
const ExpenseIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["expense", searchTerm, page, rowsPerPage],
    `shop/expenses/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("shop/expenses", token);

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const expenses = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : expenses.length;

  if (isError) {
    return <ErrorPage message={t("expense.failedToLoadExpenses")} />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("expense.expenseList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("expense.addExpense")}
        </Button>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("expense.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>

      {/* Search Field */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("expense.searchExpenses")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Expense Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="expense table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("expense.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.category")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.expenseBy")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.amount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.currency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.account", "Account")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.exchangeRate", "Exchange Rate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.convertedAmount", "Converted Amount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.paymentDate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.notes")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("expense.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Category",
                      "Expense By",
                      "Amount",
                      "Currency",
                      "Account",
                      "Exchange Rate",
                      "Converted Amount",
                      "Payment Date",
                      "Notes",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : expenses.map((expense, index) => (
                  <TableRow key={expense.id} hover>
                    <TableCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      {expense?.category_details?.name || "--"}
                    </TableCell>
                    <TableCell>
                      {expense.expense_by || t("expense.unknownExpenseBy")}
                    </TableCell>
                    <TableCell>{expense.amount || "--"}</TableCell>
                    <TableCell>
                      {expense?.currency_details?.title || "--"}
                    </TableCell>
                    <TableCell>
                      {expense?.account_details?.name || "--"}
                    </TableCell>
                    <TableCell>{expense.exchange_rate || "--"}</TableCell>
                    <TableCell>{expense.converted_amount || "--"}</TableCell>
                    <TableCell>
                      {expense.payment_date
                        ? formatJalaliDateTime(expense.payment_date)
                        : "--"}
                    </TableCell>
                    <TableCell>{expense.notes || "--"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(expense);
                          setOpenEdit(true);
                        }}
                      >
                        {t("expense.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(expense.id)}
                      >
                        {t("expense.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center">
          <Typography>{t("expense.rowsPerPage", "Rows Per Page")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
        />
      </Grid>

      {openAdd && (
        <ExpenseAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}

      {openEdit && (
        <ExpenseEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}

      <ConfirmDialog />
    </Box>
  );
};

export default ExpenseIndex;
